import React, {
  useCallback,
  useMemo,
  ChangeEvent,
  useState,
  SetStateAction,
  Dispatch,
} from 'react';
import { IStyle } from 'Styles';
import '../sections/style.scss';
import { handleTableFilter, BaseGroup } from '../sections/baseGroup';
import { TreeNodeAccessRightDTO } from 'business/models/Models';
import { NodeAccessRightsType } from './index';
import { getNewId } from 'utils/Global';
import { AccessRightType } from 'constants/';

interface Props {
  state: NodeAccessRightsType;
  setState: Dispatch<SetStateAction<NodeAccessRightsType>>;
  nodeTitle: string;
  currentUser: any;
}

interface ITableColumn {
  accessor?: any;
  Filter?: any;
  Header?: any;
}

const tableColumns: ITableColumn[] = [
  {
    accessor: 'type',
    Filter: handleTableFilter('Type'),
  },
  {
    accessor: 'name',
    Filter: handleTableFilter('Name'),
  },
  {
    accessor: 'membersCount',
    Filter: handleTableFilter('No of Members'),
  },
  {
    accessor: 'readAccess',
    Header: 'Read Access',
  },
  {
    accessor: 'modifyAccess',
    Header: 'Modify Access',
  },
  ,
  {
    accessor: 'inheritAccess',
    Header: 'Inherit Access',
  },
];

const IsAdminGroup = (groupName) => {
  return groupName === 'Administrators';
};

const modifyAccessObjectChecked = (access) => ({ id: access, init: null, toJSON: null });

const UserGroupWithAccess = (props: Props) => {
  const { state, setState, currentUser } = props;
  const [selectedRows, setSelectedRows] = useState<number[]>([]);

  const handleChangeAccessCheckbox = useCallback(
    (
      event: ChangeEvent<HTMLInputElement>,
      accessRight: TreeNodeAccessRightDTO,
      accessTypeId: number,
    ) => {
      let _withAccessRightsState = [...state.data.withAccessRights];

      _withAccessRightsState = _withAccessRightsState.map((_accessRight) => {
        if (
          (_accessRight.group && _accessRight.group.id === accessRight.group?.id) ||
          (_accessRight.user && _accessRight.user.id === accessRight.user?.id)
        ) {
          if (event.target.checked) {
            const otherAccessRights = _accessRight.accessRight.filter(
              (x) => x.id !== accessTypeId,
            );
            _accessRight.accessRight = [
              ...otherAccessRights,
              modifyAccessObjectChecked(accessTypeId),
            ];
          } else {
            _accessRight.accessRight = _accessRight.accessRight.filter(
              (x) => x.id !== accessTypeId,
            );
          }
        }
        return _accessRight;
      });
      setState((prv) => ({
        ...prv,
        data: { ...prv.data, withAccessRights: _withAccessRightsState },
      }));
    },
    [state, selectedRows],
  );

  const createAccessCheckbox = useCallback(
    (accessRight, accessType, inheritedAccessRightId, disableCheckBox) => (
      <input
        type="checkbox"
        key={`key${getNewId()}`}
        id={`ID${getNewId()}`}
        checked={accessRight.accessRight?.some((x) => x.id >= accessType)}
        onChange={(e) => handleChangeAccessCheckbox(e, accessRight, accessType)}
        disabled={
          accessType <= inheritedAccessRightId ||
          disableCheckBox ||
          accessRight.accessRight?.some((x) => x.id > accessType)
        }
      />
    ),
    [state, selectedRows],
  );

  const accessRightsData = useMemo(
    () =>
      state.data?.withAccessRights?.map((accessRight) => {
        const inheritedAccessRight = accessRight?.inheritedAccessRight?.reduce(
          (max, item) => (item.id > 1 && item.id > (max?.id ?? -Infinity) ? item : max),
          null,
        );
        return {
          type: accessRight.group?.id ? 'Group' : 'User',
          name: accessRight.group?.name || accessRight.user.name,
          membersCount: accessRight.group ? accessRight.group.users.length : '-',
          readAccess: createAccessCheckbox(
            accessRight,
            AccessRightType.READ,
            inheritedAccessRight?.id,
            IsAdminGroup(accessRight.group?.name),
          ),
          modifyAccess: createAccessCheckbox(
            accessRight,
            AccessRightType.MODIFY,
            inheritedAccessRight?.id,
            IsAdminGroup(accessRight.group?.name),
          ),
          inheritAccess: inheritedAccessRight ? inheritedAccessRight.name : 'None',
        };
      }) || [],
    [state, selectedRows, currentUser],
  );

  const handleDeleteRow = useCallback(() => {
    const _pushToWithoutAccessGroups = [];
    const _pushToWithoutAccessUser = [];
    const _withAccessRights: any[] = state.data.withAccessRights.filter((_data, _ix) => {
      if (!selectedRows.includes(_ix)) {
        return true;
      }

      _data.group?.id
        ? _pushToWithoutAccessGroups.push(_data.group)
        : _pushToWithoutAccessUser.push(_data.user);
      return false;
    });
    setSelectedRows([]);

    setState((prv) => ({
      ...prv,
      data: {
        ...prv.data,
        withAccessRights: _withAccessRights,
        groupsWithoutAccess: [
          ...prv.data.groupsWithoutAccess,
          ..._pushToWithoutAccessGroups,
        ],
        usersWithoutAccess: [...prv.data.usersWithoutAccess, ..._pushToWithoutAccessUser],
      },
    }));
  }, [selectedRows]);

  const handleSelectionChange = useCallback((rowIndices) => {
    setSelectedRows([...rowIndices]);
  }, []);

  return (
    <div id="usergroupwithaccess-wrapper" style={styles.root}>
      <div id="usergroupwithaccess-manageaccess-title" className="font-bold">
        Manage Access Right
      </div>
      <BaseGroup
        actionContainerProps={{
          onDeleteClick: handleDeleteRow,
          disableOnDeleteClick: !selectedRows.length,
        }}
        headerTitle={'Groups/Users'}
        tableProps={{
          columns: tableColumns,
          data: accessRightsData,
          onCheckboxSelectionChange: handleSelectionChange,
          selectedIds: selectedRows,
        }}
        tableContainerStyle={styles.tableContainer}
      />
    </div>
  );
};

const styles: IStyle = {
  root: {
    flexDirection: 'row',
  },
  headerContainer: {
    padding: 20,
    paddingTop: 8,
  },
  headerTitle: {
    fontWeight: 'bold',
  },
  tableContainer: {
    height: '350px',
  },
};

export default UserGroupWithAccess;
